import { Icon } from "@iconify/react/dist/iconify.js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { apiCall } from "../../Apis/apiCall";
import LoadingComponent from "../../components/ui/LoadingComponent";
import TableDataNoAvailable from "../../components/ui/TableDataNoAvailable";
import PaginationControls from "../../components/ui/PaginationControls";
import Breadcrumb from "../../components/Breadcrumb";
import CardContainer from "../../components/ui/CardContainer";
import PerPageDropdown from "../../components/ui/PerPageDropdown";
import ErrorAlert from "../../components/ui/ErrorAlert";
import SortIcons from "../../components/ui/SortIcons";
import StatusBadge from "../../components/ui/StatusBadge";
import { TextWrap } from "../../utils/textWrap";
import { AddButton } from "../../components/ui/Button";
import { handleApiResponse, handleCustomResponse } from "../../utils/response";
import InfoModal from "../../components/ui/InfoeModal";
import WorkflowGuide from "../../components/WorkflowGuide"
import { usePermissions } from "../../context/PermissionContext";
const WorkFlow = () => {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalType, setModalType] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [workflowId, setWorkflowId] = useState(null);
  const [workflowName, setWorkflowName] = useState("");
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: "asc",
  });
  const [showInfoModal, setShowInfoModal] = useState(false); // 👈 NEW: Info Modal State
  const searchInputRef = useRef(null);
  const navigate = useNavigate();
  const progressBar = useLocation();
  const fromWorkflowListGuide = progressBar.state?.progressBarNavigate || false;
const {
    hasPermission,
  } = usePermissions();
  useEffect(() => {
    if (!loading && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [loading]);

  const fetchWorkflows = async (
    page = 1,
    perPage = 10,
    search = "",
    sortBy = null,
    sortOrder = "asc"
  ) => {
    setLoading(true);
    setError(null);
    try {
      const fieldMapping = {
        workflow_name: "name",
        created_by: "created_by",
        is_active: "is_active",
      };

      const queryParams = new URLSearchParams({
        page,
        per_page: perPage,
        ...(search && { search: search }),
        ...(sortBy && { sort_by: fieldMapping[sortBy] || sortBy }),
        ...(sortOrder && { sort_order: sortOrder }),
      });

      const response = await apiCall(
        "get",
        `/get-all-workflows?${queryParams.toString()}`,
        null,
        navigate
      );

      if (response?.data?.success) {
        const { data = [], pagination = {} } = response.data;
        const {
          current_page = page,
          per_page = perPage,
          total = 0,
        } = pagination;
        const mappedWorkflows = data.map((workflow, index) => ({
          ...workflow,
          id: workflow.workflow_id || index + 1,
          workflow_name: workflow.name || "-",
          created_by: workflow.client_name || workflow.created_by || "-",
          is_active:
            workflow.is_active !== undefined && workflow.is_active !== null
              ? workflow.is_active
                ? "Active"
                : "Inactive"
              : "-",
        }));
        setWorkflows(mappedWorkflows);
        setPagination({
          currentPage: parseInt(current_page, 10),
          perPage: parseInt(per_page, 10),
          total,
        });
      } else {
        throw new Error("No workflows found.");
      }
      handleApiResponse(response.data, "fetch workflows", "GET");
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to fetch workflows.";
      setError(errorMessage);
      setWorkflows([]);
      setPagination((prev) => ({ ...prev, total: 0, currentPage: 1 }));
      const err = error.response?.data || error.message;
      handleApiResponse(err, "Failed to fetch workflows.", "GET");
    } finally {
      setLoading(false);
    }
  };

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  const debouncedSearch = useCallback(
    debounce((query) => {
      fetchWorkflows(
        1,
        pagination.perPage,
        query,
        sortConfig.field,
        sortConfig.direction
      );
    }, 500),
    [pagination.perPage, sortConfig.field, sortConfig.direction]
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    debouncedSearch(value);
  };

  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction:
        prev.field === field && prev.direction === "asc" ? "desc" : "asc",
    }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const sortedWorkflows = useMemo(() => {
    return [...workflows].sort((a, b) => {
      if (!sortConfig.field) return 0;
      let valueA = a[sortConfig.field] ?? "";
      let valueB = b[sortConfig.field] ?? "";
      if (sortConfig.field === "is_active") {
        valueA = valueA === "Active" ? 1 : 0;
        valueB = valueB === "Active" ? 1 : 0;
        return sortConfig.direction === "asc"
          ? valueA - valueB
          : valueB - valueA;
      }
      return sortConfig.direction === "asc"
        ? valueA.toString().localeCompare(valueB.toString(), undefined, {
            sensitivity: "base",
          })
        : valueB.toString().localeCompare(valueA.toString(), undefined, {
            sensitivity: "base",
          });
    });
  }, [workflows, sortConfig]);

  useEffect(() => {
    fetchWorkflows(
      pagination.currentPage,
      pagination.perPage,
      searchQuery,
      sortConfig.field,
      sortConfig.direction
    );
  }, [
    pagination.currentPage,
    pagination.perPage,
    sortConfig.field,
    sortConfig.direction,
  ]);

  const handleOpenModal = (type, workflow = null) => {
    setModalType(type);
    setShowModal(true);
    setWorkflowId(null);
    setWorkflowName("");
    setSelectedWorkflow(null);

    if (type === "edit" && workflow) {
      if (!workflow.workflow_id || isNaN(workflow.workflow_id)) {
        handleCustomResponse(
          "error",
          "Invalid or missing Workflow ID for this workflow."
        );

        return;
      }
      setSelectedWorkflow(workflow);
      setWorkflowId(workflow.workflow_id);
      setWorkflowName(workflow.workflow_name || "");
    } else if ((type === "delete" || type === "duplicate") && workflow) {
      if (!workflow.workflow_id || isNaN(workflow.workflow_id)) {
        handleCustomResponse("error", `Cannot ${type}: Invalid Workflow ID.`);
        return;
      }
      setSelectedWorkflow(workflow);
      setWorkflowId(workflow.workflow_id);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalType("");
    setWorkflowId(null);
    setWorkflowName("");
    setSelectedWorkflow(null);
  };

  const handleAddWorkflow = async () => {
    if (!workflowName.trim()) {
      handleCustomResponse("error", "Workflow name is required.");

      return;
    }

    try {
      setLoading(true);
      const response = await apiCall(
        "post",
        "/create-workflow",
        { workflow_name: workflowName },
        navigate
      );

      if (response?.data?.success) {
        handleApiResponse(
          response?.data,
          "Workflow added successfully!",
          "POST"
        );

        setWorkflowName("");
        const workflowData = response?.data?.workflow;
        const workflow = {
          workflow_id: workflowData.workflow_id,
          name: workflowData.workflow_name,
        };
        navigate("/workflow-edit", {
          state: { workflow },
        });
        await fetchWorkflows(
          pagination.currentPage,
          pagination.perPage,
          searchQuery
        );
        handleCloseModal();
      } else {
        handleApiResponse(response?.data, "Failed to add workflow.", "POST");
      }
    } catch (error) {
      const err = error.response?.data || error.message;
      handleApiResponse(err, "Failed to add workflow.", "POST");
    } finally {
      setLoading(false);
    }
  };

  const handleEditWorkflow = async () => {
    if (!workflowId || isNaN(workflowId)) {
      handleCustomResponse("error", "Invalid or missing Workflow ID.");
      return;
    }
    if (!workflowName.trim()) {
      handleCustomResponse("error", "Workflow name is required.");
      return;
    }

    try {
      setLoading(true);
      const response = await apiCall(
        "put",
        `/update-workflow-name/${workflowId}`,
        { workflow_name: workflowName, is_active: true },
        navigate
      );

      if (response?.data?.success) {
        handleApiResponse(
          response?.data,
          "Workflow updated successfully!",
          "PUT"
        );

        await fetchWorkflows(
          pagination.currentPage,
          pagination.perPage,
          searchQuery
        );
        handleCloseModal();
      } else {
        handleApiResponse(response?.data, "Failed to update workflow.", "PUT");
      }
    } catch (error) {
      const err = error.response?.data || error.message;
      handleApiResponse(err, "Failed to update workflow.", "PUT");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteWorkflow = async () => {
    if (!workflowId || isNaN(workflowId) || workflowId === 1) {
      handleCustomResponse("error", "Cannot delete: Invalid Workflow ID.");
      return;
    }

    try {
      setLoading(true);
      const response = await apiCall(
        "post",
        `/delete-workflow/${workflowId}`,
        null,
        navigate
      );

      if (response?.data?.success) {
        handleApiResponse(response?.data, "delete workflow.", "DELETE");

        await fetchWorkflows(
          pagination.currentPage,
          pagination.perPage,
          searchQuery
        );
        handleCloseModal();
      } else {
        handleApiResponse(
          response?.data,
          "Failed to delete workflow.",
          "DELETE"
        );
      }
    } catch (error) {
      const err = error.response?.data || error.message;

      handleApiResponse(err, "Failed to delete workflow.", "DELETE");
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateWorkflow = async () => {
    if (!workflowId || isNaN(workflowId)) {
      handleCustomResponse("error", "Cannot duplicate: Invalid Workflow ID.");

      return;
    }

    try {
      setLoading(true);
      const response = await apiCall(
        "post",
        `/duplicate-workflow/${workflowId}`,
        null,
        navigate
      );

      if (response?.data?.success) {
        handleApiResponse(
          response?.data,
          "Workflow duplicated successfully!",
          "POST"
        );

        await fetchWorkflows(
          pagination.currentPage,
          pagination.perPage,
          searchQuery
        );
        handleCloseModal();
      } else {
        handleApiResponse(
          response?.data,
          "Failed to duplicate workflow.",
          "POST"
        );
      }
    } catch (error) {
      const err = error.response?.data || error.message;

      handleApiResponse(err, "Failed to duplicate workflow.", "POST");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    if (modalType === "add") handleAddWorkflow();
    else if (modalType === "edit") handleEditWorkflow();
    else if (modalType === "delete") handleDeleteWorkflow();
    else if (modalType === "duplicate") handleDuplicateWorkflow();
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  };

  const handlePerPageChange = (value) => {
    setPagination((prev) => ({ ...prev, perPage: value, currentPage: 1 }));
  };

  // 👈 NEW: Info Modal Handlers
  const handleInfoClick = () => {
    setShowInfoModal(true);
  };

  const handleInfoClose = () => {
    setShowInfoModal(false);
  };

  if (error) {
    return (
      <ErrorAlert
        error={error}
        onRetry={() =>
          fetchWorkflows(
            pagination.currentPage,
            pagination.perPage,
            searchQuery
          )
        }
      />
    );
  }

  const headerContent = (
    <div className="container-fluid">
      <div className="row align-items-center">
        <div className="col-12 col-md-6 mb-3 mb-md-0">
          <div className="d-flex align-items-center flex-wrap gap-3">
            <div className="navbar-search flex-grow-1">
              <input
                type="text"
                className="form-control h-40-px w-100"
                name="search"
                placeholder="Search by workflow name..."
                value={searchQuery}
                onChange={handleSearchChange}
                ref={searchInputRef}
                aria-label="Search workflows"
              />
              <i className="bi bi-search icon"></i>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 d-flex align-items-center justify-content-between">
          <PerPageDropdown
            perPage={pagination.perPage}
            handlePerPageChange={handlePerPageChange}
          />
          <div className="d-flex align-items-center gap-2">
            {" "}
            {/* 👈 UPDATED: Added Info Button */}
            <button
              type="button"
              className="bg-info-50 text-info-600 bg-hover-info-100 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title="Information about the Workflow List Page"
              onClick={handleInfoClick}
              aria-label="Page Information"
            >
              <Icon icon="lucide:info" className="menu-icon" />
            </button>
            <AddButton
              label="Add New Workflow"
              permissionKey="add_workflow"
              onClick={() => handleOpenModal("add")}
              className="add-workflow-btn"
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Breadcrumb title="WorkFlow List" />
      <CardContainer
        headerContent={headerContent}
        paginationControls={
          <PaginationControls
            pagination={pagination}
            handlePageChange={handlePageChange}
          />
        }
        loading={loading}
      >
        <div
          className="position-relative"
          style={{
            opacity: loading ? 0.4 : 1,
            pointerEvents: loading ? "none" : "auto",
          }}
        >
          {sortedWorkflows.length === 0 && !loading ? (
            <TableDataNoAvailable
              message={
                searchQuery
                  ? "No workflows match your search."
                  : "No workflows found."
              }
            />
          ) : (
            <table
              className="table bordered-table sm-table mb-0 table-hover"
              aria-label="Workflows table"
            >
              <thead>
                <tr>
                  <th>S.No</th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("workflow_name")}
                    role="button"
                    aria-label={`Sort by workflow name, currently sorted ${
                      sortConfig.field === "workflow_name"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Workflow Name
                      <SortIcons
                        field="workflow_name"
                        sortConfig={sortConfig}
                      />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("created_by")}
                    role="button"
                    aria-label={`Sort by created by, currently sorted ${
                      sortConfig.field === "created_by"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Created By
                      <SortIcons field="created_by" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="text-center cursor-pointer"
                    onClick={() => handleSort("is_active")}
                    role="button"
                    aria-label={`Sort by status, currently sorted ${
                      sortConfig.field === "is_active"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex ">
                      Status
                      <SortIcons field="is_active" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th scope="col" className="text-center">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedWorkflows.map((workflow, index) => (
                  <tr key={workflow.workflow_id || index}>
                    <td>
                      {(pagination.currentPage - 1) * pagination.perPage +
                        index +
                        1}
                    </td>
                    <TextWrap location={workflow.workflow_name} />
                    <TextWrap location={workflow.created_by} />
                    <td className="">
                      <StatusBadge status={workflow.is_active} />
                    </td>
                    <td className="text-center">
                      <div className="d-flex align-items-center gap-10 justify-content-center">
                        {hasPermission("add_workflow") && ( <button
                          className="workflow-edit-btn bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          onClick={() =>
                            navigate("/workflow-edit", {
                              state: { workflow, fromWorkflowListGuide },
                            })
                          }
                          title={`Edit workflow ${workflow.workflow_name}`}
                          aria-label={`Edit workflow ${
                            workflow.workflow_name || "-"
                          }`}
                        >
                          <Icon
                            icon="material-symbols:arrow-forward"
                            className="menu-icon"
                          />
                        </button>)}
                        {hasPermission("add_workflow") && (<button
                          className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          onClick={() => handleOpenModal("edit", workflow)}
                          title={`Edit workflow name ${workflow.workflow_name}`}
                          aria-label={`Edit workflow name ${
                            workflow.workflow_name || "-"
                          }`}
                        >
                          <Icon icon="lucide:edit" className="menu-icon" />
                        </button>)}
                        {hasPermission("add_workflow") && ( <button
                          className="bg-success-focus text-primary-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          onClick={() => handleOpenModal("duplicate", workflow)}
                          title={`Duplicate workflow ${workflow.workflow_name}`}
                          aria-label={`Duplicate workflow ${
                            workflow.workflow_name || "-"
                          }`}
                        >
                          <Icon
                            icon="mdi:content-duplicate"
                            className="menu-icon"
                          />
                        </button>)}
                       
                        {hasPermission("delete_workflow") && (<button
                          className="bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          onClick={() => handleOpenModal("delete", workflow)}
                          title={`Delete workflow ${workflow.workflow_name}`}
                          aria-label={`Delete workflow ${
                            workflow.workflow_name || "-"
                          }`}
                        >
                          <Icon
                            icon="fluent:delete-24-regular"
                            className="menu-icon"
                          />
                        </button>)}
                       
                        
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {loading && (
          <div
            className="position-absolute top-50 start-50 translate-middle d-flex justify-content-center align-items-center"
            style={{
              zIndex: 10,
              borderRadius: "10px",
              padding: "20px",
            }}
          >
            <LoadingComponent />
          </div>
        )}
      </CardContainer>

      {/* 👈 EXISTING: Main Modal (Add/Edit/Delete/Duplicate) */}
      <div
        className={`modal fade ${showModal ? "show d-block" : ""}`}
        tabIndex="-1"
        role="dialog"
        style={{ display: showModal ? "block" : "none", backgroundColor: showModal ? "rgba(0,0,0,0.5)" : "" }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {modalType === "add"
                  ? "Add New Workflow"
                  : modalType === "edit"
                  ? "Edit Workflow"
                  : modalType === "delete"
                  ? "Confirm Deletion"
                  : "Duplicate Workflow"}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseModal}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              {modalType === "delete" || modalType === "duplicate" ? (
                <p className="text-neutral-900">
                  Are you sure you want to {modalType}{" "}
                  <strong>
                    {selectedWorkflow?.workflow_name || "this workflow"}
                  </strong>
                  ?
                </p>
              ) : (
                <div className="mb-3">
                  <label htmlFor="workflowName" className="form-label">
                    Workflow Name *
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="workflowName"
                    value={workflowName}
                    onChange={(e) => setWorkflowName(e.target.value)}
                    placeholder="Enter workflow name"
                    aria-label="Workflow name"
                  />
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCloseModal}
                aria-label="Cancel"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`btn btn-${
                  modalType === "delete" ? "danger" : "primary-600"
                } d-flex align-items-center`}
                onClick={handleSubmit}
                disabled={loading}
                aria-label={
                  modalType === "add"
                    ? "Add Workflow"
                    : modalType === "edit"
                    ? "Update Workflow"
                    : modalType === "delete"
                    ? "Delete Workflow"
                    : "Duplicate Workflow"
                }
              >
                {loading
                  ? "Please wait..."
                  : modalType === "add"
                  ? "Add Workflow"
                  : modalType === "edit"
                  ? "Update Workflow"
                  : modalType === "delete"
                  ? "Delete"
                  : "Duplicate"}
              </button>
            </div>
          </div>
        </div>
      </div>
      {showModal && <div className="no-modal fade show"></div>}

      {/* 👈 NEW: Info Modal - EXACTLY SAME AS Previous Pages */}
      <InfoModal
        isOpen={showInfoModal}
        onClose={handleInfoClose}
        title="Workflow Management - Complete Overview"
      >
        <p className="text-neutral-900 mb-4">
          This is the <strong>master control panel</strong> for all{" "}
          <strong>Workflows</strong> — the complete step-by-step process your
          team follows from the moment a vehicle arrives until it leaves.
        </p>

        <h6 className="fw-bold text-primary-600 mb-3">
          What Exactly is a Workflow?
        </h6>
        <p className="mb-4">
          A workflow defines everything about how parking/valet/ operations
          happen:
        </p>
        <ul className="list-group list-group-flush mb-4">
          <li className="list-group-item">
            Sequence of steps (e.g., Receive Car → Inspection → Park → Payment →
            Delivery)
          </li>
          <li className="list-group-item">
            Which fields are required in each step (ticket no, photos, damage
            notes, customer signature, etc.)
          </li>
          <li className="list-group-item">
            Which staff role handles each step
          </li>
          <li className="list-group-item">
            Which branch or location this workflow applies to
          </li>
          <li className="list-group-item">
            Fully customizable — create unlimited versions for different
            branches, services, or VIP customers
          </li>
        </ul>

        <h6 className="fw-bold text-primary-600 mb-3">Add New Workflow</h6>
        <p className="small text-neutral-600 mb-2">
          Click "Add New Workflow" → Enter a name → You will be redirected to
          the powerful Workflow Builder where you can:
        </p>
        <ul className="list-group list-group-flush mb-4">
          <li className="list-group-item">
            Add, remove, reorder, or rename steps
          </li>
          <li className="list-group-item">
            Add any type of field: text, number, photo, dropdown, checkbox,
            signature, etc.
          </li>
          <li className="list-group-item">Make fields mandatory or optional</li>
          <li className="list-group-item">
            Assign steps to specific staff roles
          </li>
          <li className="list-group-item">
            Later assign the workflow to branches & locations
          </li>
        </ul>

        <h6 className="fw-bold text-primary-600 mb-3">
          Four Powerful Action Buttons (Per Workflow)
        </h6>
        <ul className="list-group list-group-flush mb-4">
          <li className="list-group-item">
            <strong>Forward Arrow → Open Workflow Editor</strong>
            <br />
            The main button! Opens the full visual builder to design the entire
            process from scratch or edit existing logic.
          </li>
          <li className="list-group-item">
            <strong>Edit (Pencil Icon)</strong>
            <br />
            Quickly change only the workflow name or basic description (does NOT
            open full editor)
          </li>
          <li className="list-group-item">
            <strong>Duplicate (Copy Icon)</strong>
            <br />
            Creates a complete copy of the workflow with all steps & fields
            <br />
            <span className="text-success-600 fw-bold">
              Best for creating variations
            </span>{" "}
            (e.g., VIP Workflow, Night Shift, etc.)
            <br />
            <small className="text-danger-600">
              Note: Branch/location assignments are NOT copied
            </small>
          </li>
          <li className="list-group-item">
            <strong>Delete (Red Trash)</strong>
            <br />
            Permanently removes the workflow
            <br />
            <span className="text-danger fw-bold">
              Confirmation popup appears — cannot be undone
            </span>
          </li>
        </ul>

        <h6 className="fw-bold text-primary-600 mb-3">
          Modal Types You Will See
        </h6>
        <ul className="list-group list-group-flush mb-4">
          <li className="list-group-item">
            <strong>Add Workflow Modal</strong> → Just enter name → Go to
            builder
          </li>
          <li className="list-group-item">
            <strong>Edit Name Modal</strong> → Quick name change only
          </li>
          <li className="list-group-item">
            <strong>Duplicate Modal</strong> → Confirm & get instant copy
          </li>
          <li className="list-group-item">
            <strong>Delete Confirmation Modal</strong> → Double-check before
            permanent delete
          </li>
        </ul>

        <div className="alert alert-info small mt-4">
          <strong>Important Notes:</strong>
          <br />• Changes in workflow affect only <strong>new</strong> parking
          sessions
          <br />
          • You can have 100+ workflows — no limit
          <br />
          • Duplicating saves hours of work when creating similar flows
          <br />• Always test a new workflow on one location first before
          rolling out everywhere
        </div>

        <p className="text-neutral-900 mt-4 fw-bold text-primary">
          Pro Tip: Never build from scratch → Always DUPLICATE your best working
          workflow → Make small changes → Save as new version → Assign → Done in
          5 minutes!
        </p>

        <p className="text-neutral-900 small">
          Business Impact: Perfect workflows = Zero confusion, faster service,
          no missing data, consistent quality, automatic reports, and extremely
          satisfied customers & staff.
        </p>
      </InfoModal>
      {
        !loading && <WorkflowGuide/>
      }
    </>
  );
};

export default WorkFlow;